import type { LocationRef } from "@opencode/client"
import { activeSessionId, agents, providers, sessions } from "./session-state"
import { uiState, getAgentModelPreference } from "./preferences"
import { instances } from "./instances"
import { getRootClient } from "./opencode-client"
import { resolveAgentId } from "../types/session"
import { requestLocationOptions, toRequestLocation } from "./request-locations"

const DEFAULT_MODEL_OUTPUT_LIMIT = 32_000

function getActiveCatalogLocation(instanceId: string): LocationRef {
  const instance = instances().get(instanceId)
  const selectedId = activeSessionId().get(instanceId)
  const selectedLocation = selectedId && selectedId !== "info"
    ? sessions().get(instanceId)?.get(selectedId)?.location
    : undefined
  return selectedLocation ?? { directory: instance?.folder ?? "" }
}

function isModelValid(
  instanceId: string,
  model?: { providerId: string; modelId: string } | null,
): model is { providerId: string; modelId: string } {
  if (!model?.providerId || !model.modelId) return false
  const instanceProviders = providers().get(instanceId) || []
  const provider = instanceProviders.find((p) => p.id === model.providerId)
  if (!provider) return false
  return provider.models.some((item) => item.id === model.modelId)
}

function getRecentModelPreferenceForInstance(
  instanceId: string,
): { providerId: string; modelId: string } | undefined {
  const recents = uiState().models.recents ?? []
  for (const item of recents) {
    if (isModelValid(instanceId, item)) {
      return item
    }
  }
}

async function getDefaultModel(
  instanceId: string,
  agentName?: string,
): Promise<{ providerId: string; modelId: string }> {
  const instanceProviders = providers().get(instanceId) || []
  const instanceAgents = agents().get(instanceId) || []
  const agentId = agentName ? resolveAgentId(instanceAgents, agentName) : ""

  if (agentId) {
    const agent = instanceAgents.find((a) => a.id === agentId)
    if (agent && agent.model && isModelValid(instanceId, agent.model)) {
      return {
        providerId: agent.model.providerId,
        modelId: agent.model.modelId,
      }
    }

    const stored = await getAgentModelPreference(instanceId, agentId)
    if (isModelValid(instanceId, stored)) {
      return stored
    }
  }

  const recent = getRecentModelPreferenceForInstance(instanceId)
  if (recent) {
    return recent
  }

  for (const provider of instanceProviders) {
    if (provider.defaultModelId) {
      const model = provider.models.find((m) => m.id === provider.defaultModelId)
      if (model) {
        return {
          providerId: provider.id,
          modelId: model.id,
        }
      }
    }
  }

  if (instanceProviders.length > 0) {
    const firstProvider = instanceProviders[0]
    const firstModel = firstProvider.models[0]
    if (firstModel) {
      return {
        providerId: firstProvider.id,
        modelId: firstModel.id,
      }
    }
  }

  if (!instanceProviders.some((provider) => provider.models.length > 0)) {
    const instance = instances().get(instanceId)
    if (instance?.client) {
      const response = await getRootClient(instanceId).model.default({
        location: toRequestLocation(getActiveCatalogLocation(instanceId)),
      }, requestLocationOptions(getActiveCatalogLocation(instanceId))).catch(() => null)
      if (response?.data) {
        return { providerId: response.data.providerID, modelId: response.data.id }
      }
    }
  }

  return { providerId: "", modelId: "" }
}

export { DEFAULT_MODEL_OUTPUT_LIMIT, getActiveCatalogLocation, getDefaultModel, getRecentModelPreferenceForInstance, isModelValid }
